import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogProps,
  AlertDialogTitle,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { ADJUST_MILESTONE_TIME } from '@optools/services/operator-gateway/milestones/mutations';
import { GET_MILESTONES } from '@optools/services/operator-gateway/milestones/queries';
import { EditorMilestone } from '@optools/services/operator-gateway/milestones/types';
import { formatSecondsHHMMSS } from '@optools/utils/format';
import { add } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { formatInningDetails } from '../utils/milestones';

export type AdjustMilestoneDialogProps = Required<
  Pick<AlertDialogProps, 'open' | 'onOpenChange'>
> & {
  activeMilestone: EditorMilestone | null;
  mediaId: string;
  programDateTime: string;
  requestedTime: number | undefined;
};

export function AdjustMilestoneDialog({
  activeMilestone,
  mediaId,
  onOpenChange,
  open,
  programDateTime,
  requestedTime,
}: AdjustMilestoneDialogProps) {
  // Storing local references to avoid content changing in modal while it is closing
  const [_activeMilestone, setActiveMilestone] = useState(activeMilestone);
  const [_requestedTime, setRequestedTime] = useState(requestedTime);

  useEffect(() => {
    if (open && activeMilestone && typeof requestedTime === 'number') {
      setActiveMilestone(activeMilestone);
      setRequestedTime(requestedTime);
    }
  }, [activeMilestone, open, requestedTime]);

  const [adjustMilestone, { loading }] = useMutation(ADJUST_MILESTONE_TIME, {
    onCompleted: ({ adjustMilestoneTime }) => {
      if (!adjustMilestoneTime) {
        toast.error('Adjust Milestone Error', { duration: Infinity });
        return;
      }
      onOpenChange(false);
    },
    refetchQueries: [{ query: GET_MILESTONES, variables: { mediaId } }],
  });

  const handleAdjustClick = () => {
    if (!activeMilestone || typeof requestedTime !== 'number') return;
    const timestamp = add(programDateTime, { seconds: requestedTime }).toISOString();

    adjustMilestone({
      variables: { mediaId, milestoneId: activeMilestone.id, timestamp },
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!loading) onOpenChange(nextOpen);
  };

  const { id, milestoneType, videoTime } = _activeMilestone ?? {};

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="min-w-max">
        <AlertDialogHeader>
          <AlertDialogTitle>Adjust Milestone</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription className="sr-only">
          adjust selected milestone
        </AlertDialogDescription>

        {/*Dialog Body*/}
        <div className="flex flex-col gap-4">
          <dl className="flex items-center justify-start gap-2">
            <dt className="text-sm leading-none font-medium" id="adjust-milestone-id">
              Id:
            </dt>
            <dd
              aria-labelledby="adjust-milestone-id"
              className="text-sm leading-none font-medium opacity-70">
              {id}
            </dd>
          </dl>
          <dl className="flex items-center justify-start gap-2">
            <dt className="text-sm leading-none font-medium" id="adjust-milestone-type">
              Type:
            </dt>
            <dd
              aria-labelledby="adjust-milestone-type"
              className="text-sm leading-none font-medium opacity-70">
              {milestoneType}
            </dd>
          </dl>
          <div className="flex flex-wrap items-center justify-start gap-2">
            {_activeMilestone?.keywords && _activeMilestone.keywords.length > 0 && (
              <div className="flex flex-wrap items-center justify-start gap-2">
                <dt className="text-sm leading-none font-medium" id="edit-milestone-details">
                  Inning:
                </dt>
                <dd
                  aria-labelledby="edit-milestone-details"
                  className="text-sm leading-none font-medium opacity-70">
                  {formatInningDetails(_activeMilestone)}
                </dd>
              </div>
            )}
          </div>
          <div className="my-4 flex items-center justify-center">
            <div className="flex gap-4">
              <dl className="flex gap-1">
                <dt className="font-bold">Current:</dt>
                <dd>{typeof videoTime === 'number' ? formatSecondsHHMMSS(videoTime) : 'Error'}</dd>
              </dl>
              <ArrowRight className="shrink-0" />
              <dl className="flex gap-1">
                <dd className="font-bold">New:</dd>
                <dt>
                  {typeof _requestedTime === 'number'
                    ? formatSecondsHHMMSS(_requestedTime)
                    : 'Error'}
                </dt>
              </dl>
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button data-testid="submit-button" disabled={loading} onClick={handleAdjustClick}>
            Adjust
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
